import React from 'react';
import { Link } from 'react-router-dom';
import { aboutPage } from '../data/mockData';

export const AboutPage: React.FC = () => {
  return (
    <div className="bg-background-light">
      {/* Hero Section */}
      <section className="relative w-full h-[460px] flex items-center justify-center">
        <div className="absolute inset-0 bg-slate-900/50"></div>
        <img
          src={aboutPage.hero.image.src}
          alt={aboutPage.hero.image.alt}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative max-w-4xl px-6 text-center text-white">
          <div className="flex items-center justify-center gap-2 mb-6 text-sm font-medium">
            <Link to="/" className="hover:text-white/80 transition-colors">{aboutPage.hero.breadcrumb.home}</Link>
            <span className="material-symbols-outlined text-sm opacity-70">chevron_right</span>
            <span className="text-white/70">{aboutPage.hero.breadcrumb.current}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-5 tracking-tight">
            {aboutPage.hero.title}
          </h1>
          <p className="text-lg text-white/85 max-w-2xl mx-auto leading-relaxed">
            {aboutPage.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="py-20 px-6 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-5">
            <p className="text-sm font-semibold text-primary uppercase tracking-wide">
              {aboutPage.story.tag}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-text tracking-tight">
              {aboutPage.story.title}
            </h2>
            {aboutPage.story.paragraphs.map((paragraph, index) => (
              <p key={index} className="text-base lg:text-lg text-neutral-muted leading-relaxed">
                {paragraph}
              </p>
            ))}
            <div className="pt-2">
              <div className="flex items-start gap-3 p-5 border-l-4 border-primary bg-slate-50">
                <span className="material-symbols-outlined text-primary text-3xl">diversity_3</span>
                <p className="italic text-neutral-text leading-relaxed">"{aboutPage.story.quote}"</p>
              </div>
            </div>
          </div>
          <div className="relative">
            <img
              src={aboutPage.story.image.src}
              alt={aboutPage.story.image.alt}
              className="rounded-xl shadow-lg w-full object-cover aspect-[4/3]"
            />
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 px-6 lg:px-20 bg-slate-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-slate-200 flex flex-col gap-4">
            <div className="size-12 bg-primary rounded-lg flex items-center justify-center text-white">
              <span className="material-symbols-outlined text-2xl">flag</span>
            </div>
            <h3 className="text-2xl font-bold text-neutral-text">{aboutPage.missionVision.mission.title}</h3>
            <p className="text-neutral-muted leading-relaxed text-base lg:text-lg">
              {aboutPage.missionVision.mission.content}
            </p>
          </div>
          <div className="bg-primary-deep p-8 lg:p-10 rounded-xl flex flex-col gap-4 text-white">
            <div className="size-12 bg-white/15 rounded-lg flex items-center justify-center text-white">
              <span className="material-symbols-outlined text-2xl">visibility</span>
            </div>
            <h3 className="text-2xl font-bold">{aboutPage.missionVision.vision.title}</h3>
            <p className="text-white/85 leading-relaxed text-base lg:text-lg">
              {aboutPage.missionVision.vision.content}
            </p>
          </div>
        </div>
      </section>

      {/* SPEED Values Section */}
      <section className="py-20 px-6 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-text mb-4 tracking-tight">{aboutPage.values.title}</h2>
            <p className="text-base lg:text-lg text-neutral-muted leading-relaxed">{aboutPage.values.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {aboutPage.values.items.map((value) => (
              <div
                key={value.letter}
                className="p-6 rounded-xl bg-slate-50 border border-slate-200 flex flex-col items-center text-center text-center"
              >
                <div className="text-primary mb-4">
                  <span className="material-symbols-outlined text-3xl">{value.icon}</span>
                </div>
                <h4 className="text-lg font-semibold mb-2 text-neutral-text">{value.title}</h4>
                <p className="text-sm text-neutral-muted leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Team */}
      <section className="py-20 px-6 lg:px-20 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl lg:text-4xl font-bold text-neutral-text mb-3 tracking-tight">{aboutPage.team.title}</h2>
              <p className="text-base lg:text-lg text-neutral-muted leading-relaxed">{aboutPage.team.subtitle}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aboutPage.team.members.map((member) => (
              <div key={member.name} className="bg-white rounded-xl overflow-hidden border border-slate-200 group">
                <div className="h-72 overflow-hidden">
                  <img
                    src={member.image.src}
                    alt={member.image.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-7">
                  <h4 className="text-lg font-semibold text-neutral-text mb-1">{member.name}</h4>
                  <p className="text-primary font-medium text-sm mb-3">{member.role}</p>
                  <p className="text-neutral-muted text-sm leading-relaxed mb-5">{member.bio}</p>
                  <div className="flex gap-2">
                    <a className="size-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-primary hover:text-white transition-colors" href="#" aria-label={`Email ${member.name}`}>
                      <span className="material-symbols-outlined text-sm">alternate_email</span>
                    </a>
                    <a className="size-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-primary hover:text-white transition-colors" href="#" aria-label={`${member.name} social profile`}>
                      <span className="material-symbols-outlined text-sm">share</span>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-20 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 tracking-tight">{aboutPage.cta.title}</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/contact"
              className="w-full sm:w-auto bg-white text-primary font-semibold py-3.5 px-8 rounded-md hover:bg-slate-100 transition-colors text-base text-center"
            >
              {aboutPage.cta.primary}
            </Link>
            <Link
              to="/services"
              className="w-full sm:w-auto border border-white/40 text-white font-semibold py-3.5 px-8 rounded-md hover:bg-white/10 transition-colors text-base text-center"
            >
              {aboutPage.cta.secondary}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
